import {gql} from "@apollo/client";
import {capitalize, find} from "lodash";

import {getApolloClient} from "@atg-tillsammans/apollo-data-access";
import {MutableParticipantFieldsFragmentDoc} from "@atg-tillsammans/graphql/fragment/__generated__/participantFragments.graphql.generated";
import {PushedRoundStateFieldsFragmentDoc} from "@atg-tillsammans/graphql/fragment/__generated__/roundFragments.graphql.generated";
import {CORE_MEMBER_FIELDS} from "@atg-tillsammans/graphql/fragment/memberFragments.graphql";
import {
    TeamMembersDocument,
    type TeamMembersQuery,
} from "@atg-tillsammans/graphql/query/__generated__/memberQueries.graphql.generated";
import {
    RoundParticipantsDocument,
    TeamRoundBalanceDocument,
    TeamRoundDocument,
} from "@atg-tillsammans/graphql/query/__generated__/roundQueries.graphql.generated";
import {MemberModel, type TeamID} from "@atg-tillsammans/types";

import type {
    Round,
    Sales,
    Participant,
    RoundState,
    Member,
} from "@atg-tillsammans/types/generated";

export const capitalized = (text?: string): string =>
    (text && text.split(" ").map(capitalize).join(" ")) || "";

export const chatNotificationAllowed = (
    currentTeamId: number,
    channelId: number,
    dialogOpen: boolean,
    smallScreen: boolean,
) => {
    const isStandingOnDifferentTeam = currentTeamId !== channelId;
    const isAllowedOnSameTeam = !dialogOpen && smallScreen;
    return isStandingOnDifferentTeam || isAllowedOnSameTeam;
};

export function writeSalesFragment(sales: Sales) {
    const client = getApolloClient();
    if (!client) return;
    client.cache.writeFragment({
        id: `Sales:${sales.id}`,
        fragment: gql`
            fragment PushedSales on Sales {
                id
                deadline
                price
                buyLimit
                buyLimitPerPerson
                totalSalesAmount
                refundAmount
                scheduledReleases
            }
        `,
        data: {
            __typename: "Sales",
            id: sales.id,
            deadline: sales.deadline,
            price: sales.price,
            buyLimit: sales.buyLimit,
            buyLimitPerPerson: sales.buyLimitPerPerson,
            totalSalesAmount: sales.totalSalesAmount,
            refundAmount: sales.refundAmount ?? null,
            scheduledReleases: sales.scheduledReleases ?? [],
        },
    });
}

export function writeRoundBalanceQuery(
    teamId: string,
    gameId: string,
    roundBalance: number,
) {
    const client = getApolloClient();
    if (!client) return;

    client.cache.writeQuery({
        query: TeamRoundBalanceDocument,
        data: {roundBalance},
        variables: {
            teamId,
            gameId,
        },
    });
}

export function writeRoundFragment(round: Round) {
    const client = getApolloClient();
    if (!client) return;

    if (round.sales) {
        writeSalesFragment(round.sales);
    }

    client.cache.writeFragment({
        id: `Round:${round.id}`,

        fragment: gql`
            fragment PushedRound on Round {
                state
                gameInitiator
                sales
            }
        `,
        data: {
            state: round.state,
            gameInitiator: {
                __ref: `Member:${round?.gameInitiator?.id}`,
            },
            sales: {
                __ref: `Sales:${round.id}`,
            },
        },
    });
}

export function writeRoundStateFragment(roundId: string, roundState: RoundState) {
    const client = getApolloClient();
    if (!client) return;
    client.cache.writeFragment({
        id: `Round:${roundId}`,
        fragment: PushedRoundStateFieldsFragmentDoc,
        data: {
            state: roundState,
        },
    });
}

export function writeRoundParticipationsQuery(
    round: Round,
    subscriptions: Record<number, number>,
) {
    const client = getApolloClient();
    if (!client) return;

    client.cache.writeQuery({
        query: RoundParticipantsDocument,
        variables: {
            teamId: `${round.teamId}`,
            gameId: round.gameId,
        },
        data: {
            roundParticipants: Object.keys(subscriptions).map((memberId) => {
                const memberIdAsNumber = Number(memberId);

                // Get the right member to set on the participant obejct to be stored in cache
                const member = client.cache.readFragment({
                    id: `Member:${memberId}`,
                    fragment: CORE_MEMBER_FIELDS,
                });

                const sharePrice = round?.sales?.price ?? 0;
                const numberOfShares = subscriptions[memberIdAsNumber];

                return {
                    __typename: "Participant",
                    id: `${round.id}_${memberId}`,
                    teamId: Number(round.teamId),
                    gameId: round.gameId,
                    numberOfShares,
                    totalCost: sharePrice * numberOfShares,
                    member,
                };
            }),
        },
    });
}

export function updateTeamMembersQuery(incomingMember: Member, teamId: TeamID) {
    const client = getApolloClient();
    if (!client) return;

    client.cache.updateQuery<TeamMembersQuery>(
        {
            query: TeamMembersDocument,
            variables: {
                teamId: `${teamId}`,
            },
        },
        (data) => {
            const teamMembers = data?.teamMembers ?? [];
            const existingMember = find(
                teamMembers,
                (member) => member.memberId === incomingMember.memberId,
            );
            if (existingMember) {
                return {teamMembers};
            }

            return {
                teamMembers: [...teamMembers, MemberModel.toMemberGraph(incomingMember)],
            };
        },
    );
}

export function removeByUpdateTeamMembersQuery(removeMemberId: number, teamId: TeamID) {
    const client = getApolloClient();
    if (!client) return;
    client.cache.updateQuery<TeamMembersQuery>(
        {
            query: TeamMembersDocument,
            variables: {
                teamId: `${teamId}`,
            },
        },
        (data) => ({
            teamMembers: (data?.teamMembers ?? []).filter(
                (member: Member) => member.memberId !== removeMemberId,
            ),
        }),
    );
}

export function updateParticipation(participation: Participant) {
    const client = getApolloClient();
    if (!client) return;

    const participationId = client.cache.identify({
        __typename: "Participant",
        id: participation.id,
    });

    const participantFragment: Partial<Participant> | null = client.cache.readFragment({
        id: participationId,
        fragment: MutableParticipantFieldsFragmentDoc,
    });

    if (participantFragment) {
        client.cache.writeFragment({
            id: participationId,
            fragment: MutableParticipantFieldsFragmentDoc,

            data: {
                ...participantFragment,
                numberOfShares:
                    (participantFragment.numberOfShares ?? 0) +
                    (participation.numberOfShares ?? 0),
                totalCost:
                    (participantFragment.totalCost ?? 0) + (participation.totalCost ?? 0),
            },
        });
    } else {
        client.refetchQueries({
            include: [TeamRoundDocument],
        });
    }
}
